import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { fetchAutoTagResultDetail } from "../../services/taggingResultsService";
import { getAiHubStatusChipSx } from "./aiHubChipStyles";
import { AI_HUB_PATH } from "./constants";
import AiHubDetailBreadcrumb from "./AiHubDetailBreadcrumb";
import {
  AiHubDetailErrorState,
  AiHubDetailLoadingState,
} from "./AiHubDetailPageState";
import AiHubTaggingDetailHeader from "./AiHubTaggingDetailHeader";
import {
  displayOrDash,
  isTaggingResultDetailRequestReady,
  MISSING_CLASSIFICATION_DETAIL_QUERY_MESSAGE,
  useTaggingResultDetailRequest,
  type AutoTagResultDetail,
  type TagOrigin,
} from "./taggingResultDetailData";

const cellSx = {
  borderColor: "#eef0f3",
  px: 2,
  py: 1.5,
  fontSize: 13,
  color: "#374151",
  verticalAlign: "middle",
} as const;

function originChipSx(origin: TagOrigin) {
  return getAiHubStatusChipSx(origin === "Manual" ? "green" : "blue");
}

export default function TaggingResultDetailPage() {
  const navigate = useNavigate();
  const request = useTaggingResultDetailRequest("taggingResult");
  const { objectId, objectSubType, objectType, version } = request;
  const [detail, setDetail] = useState<AutoTagResultDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const returnToList = () => {
    navigate(AI_HUB_PATH, { state: { tab: "taggingResult" } });
  };

  useEffect(() => {
    let cancelled = false;
    const nextRequest = { objectId, objectSubType, objectType, version };

    if (!isTaggingResultDetailRequestReady(nextRequest)) {
      setDetail(null);
      setError(MISSING_CLASSIFICATION_DETAIL_QUERY_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetchAutoTagResultDetail(nextRequest)
      .then((next) => {
        if (!cancelled) {
          setDetail(next);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load Classification Result.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [objectId, objectSubType, objectType, version]);

  if (loading && !detail) {
    return <AiHubDetailLoadingState />;
  }

  if (error && !detail) {
    return (
      <AiHubDetailErrorState
        resultId={objectId}
        message={error}
        onBack={returnToList}
      />
    );
  }

  if (!detail) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f7f8fa",
        p: { xs: 2, md: 3 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          minHeight: "calc(100vh - 48px)",
          bgcolor: "#fff",
          borderRadius: "8px",
          p: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <AiHubDetailBreadcrumb
            currentLabel={objectId}
            onBack={returnToList}
          />
        </Box>

        <AiHubTaggingDetailHeader
          title={detail.title || objectId}
          references={detail.references}
        />

        <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #e5e7eb" }}>
          <Typography sx={{ mb: 1.5, fontSize: 16, fontWeight: 600 }}>
            Classification List
          </Typography>

          <TableContainer>
            <Table aria-label="Classification List" sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fb" }}>
                  {[
                    "Dimension",
                    "Tag Value",
                    "Triggered By",
                    "Analysis",
                    "Summary",
                    "Recommendation",
                  ].map((heading) => (
                    <TableCell
                      key={heading}
                      sx={{ ...cellSx, py: 1.25, fontWeight: 600 }}
                    >
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.tags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell sx={cellSx}>
                      {displayOrDash(tag.concept)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {displayOrDash(tag.tagValue)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        label={tag.origin}
                        size="small"
                        sx={originChipSx(tag.origin)}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 260 }}>
                      {displayOrDash(tag.analysis)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 190 }}>
                      {displayOrDash(tag.summary)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 210 }}>
                      {displayOrDash(tag.recommendation)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}
